import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallState } from '../context/CallStateContext';
import { usePractice } from '../context/PracticeContext';
import { TAB_ID } from '../utils/axiosInstance';

/**
 * Live config sync — keeps every open tab of a practice showing the same
 * configuration, the way the Tasks page keeps task lists in step.
 *
 * A page registers the loaders it already has, one per scope:
 *
 *   const { staleScopes, reloadScope, dismissScope } = useConfigSync({
 *     practice:      { reload: reloadPracticeInfo, isDirty: () => practiceTabDirty },
 *     therapy_types: { reload: refetchTherapyTypes },   // no drafts → always live
 *   });
 *
 * Behaviour per incoming event:
 *   - ignore our own writes (`origin === TAB_ID`) and events for another practice
 *   - coalesce a burst into one reload per scope (an explicit Speichern fires
 *     ~10 writes; a PVS import fires one per row)
 *   - scope NOT dirty  → reload silently. This is the Google-Drive feel, and it is
 *     the normal case: almost every control now saves itself, so almost nothing is
 *     ever "dirty".
 *   - scope IS dirty   → hold the refresh and apply it the moment the section is
 *     saved. The user is never asked to press anything. Only the spoken messages
 *     can be dirty (they stay manual so saving can re-synthesize their audio), and
 *     overwriting those mid-typing would destroy the text being written.
 *
 * On every socket (re)connect all registered scopes are refreshed unconditionally.
 * The backend drops events when nobody is connected and nothing replays them, so
 * a reconnect is the only moment we can be sure we are back in sync. This mirrors
 * the Tasks page's catch-up rule, which exists because of a real 2026-07-28
 * incident — do not add a "only if we missed something" gate here, because a tab
 * cannot know what it missed.
 *
 * @param {Object} handlers  scope -> { reload: () => void|Promise, isDirty?: () => boolean }
 */
const COALESCE_MS = 500;

export default function useConfigSync(handlers) {
  const { subscribeConfigEvents, isConnected } = useCallState();
  const { practiceId } = usePractice();

  // Handlers are re-created on every render by the calling component; keep them
  // in a ref so the subscription itself never needs to be torn down and rebuilt
  // (which would drop events in the gap).
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const timersRef = useRef({});
  // Scopes whose refresh is waiting for the user to finish an unsaved edit.
  const deferredRef = useRef(new Set());
  const [deferredTick, setDeferredTick] = useState(0);
  const practiceIdRef = useRef(practiceId);
  practiceIdRef.current = practiceId;

  const clearTimers = useCallback(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
  }, []);

  // Run one scope now. If the section has an unsaved edit that a refresh would
  // overwrite, hold the refresh until it's saved rather than prompting — the user
  // should never be asked to press anything for someone else's change.
  const applyScope = useCallback((scope) => {
    const handler = handlersRef.current?.[scope];
    if (!handler?.reload) return;

    if (handler.isDirty?.()) {
      deferredRef.current.add(scope);
      setDeferredTick((n) => n + 1);
      return;
    }
    deferredRef.current.delete(scope);
    try {
      Promise.resolve(handler.reload()).catch((err) =>
        console.error(`[CONFIG-SYNC] reload failed for scope "${scope}":`, err)
      );
    } catch (err) {
      console.error(`[CONFIG-SYNC] reload threw for scope "${scope}":`, err);
    }
  }, []);

  // Flush anything that was held back, as soon as its section stops being dirty.
  // Runs on every render — the check is a handful of string compares, and it means
  // a deferred refresh lands the moment the user saves, with nothing to click.
  useEffect(() => {
    if (!deferredRef.current.size) return;
    Array.from(deferredRef.current).forEach((scope) => {
      const handler = handlersRef.current?.[scope];
      if (!handler?.reload || handler.isDirty?.()) return;
      deferredRef.current.delete(scope);
      try {
        Promise.resolve(handler.reload()).catch((err) =>
          console.error(`[CONFIG-SYNC] deferred reload failed for scope "${scope}":`, err)
        );
      } catch (err) {
        console.error(`[CONFIG-SYNC] deferred reload threw for scope "${scope}":`, err);
      }
    });
  });

  useEffect(() => {
    if (!subscribeConfigEvents) return undefined;

    const unsubscribe = subscribeConfigEvents((data) => {
      if (!data?.scope) return;
      // Our own write — the form already shows this value.
      if (data.origin && data.origin === TAB_ID) return;
      // Late event from the practice we just switched away from.
      if (data.practice_id && practiceIdRef.current && data.practice_id !== practiceIdRef.current) return;
      if (!handlersRef.current?.[data.scope]) return;

      const { scope } = data;
      if (timersRef.current[scope]) clearTimeout(timersRef.current[scope]);
      timersRef.current[scope] = setTimeout(() => {
        delete timersRef.current[scope];
        applyScope(scope);
      }, COALESCE_MS);
    });

    return () => {
      unsubscribe?.();
    };
  }, [subscribeConfigEvents, applyScope]);

  // Catch-up on RE-connect — see the note above about why this is unconditional.
  //
  // The first open of a mount is skipped, and it has to be tracked with a "have I
  // seen one yet" flag rather than by seeding from `isConnected`: on a cold page
  // load the socket is still closed when this mounts, so seeding would treat the
  // initial open as a reconnect and reload all scopes — cancelling the initial
  // hydration that is still in flight, re-issuing every request, and discarding
  // anything typed in that window.
  //
  // Trade-off: if the page is opened while the backend is down and the socket only
  // opens later, that gap is not caught up. That is the rarer case, and the mount's
  // own load already covers the normal one.
  const seenConnectRef = useRef(false);
  useEffect(() => {
    if (!isConnected) return;
    if (!seenConnectRef.current) {
      seenConnectRef.current = true;
      return;
    }
    Object.keys(handlersRef.current || {}).forEach(applyScope);
  }, [isConnected, applyScope]);

  // Practice switch: drop pending work — the page reloads everything for the new
  // practice anyway.
  useEffect(() => {
    clearTimers();
    deferredRef.current.clear();
  }, [practiceId, clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  // `deferredTick` only exists to re-render when something gets held back, so the
  // flush effect above gets a chance to run.
  return { deferredCount: deferredRef.current.size + deferredTick * 0 };
}
